import { useId, useMemo, useState } from "react";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { ReactComponent as ArrowDownIcon } from "../../../../assets/icons/arrow-down.svg";
import { ReactComponent as CheckIcon } from "../../../../assets/icons/check.svg";

const dropdownMotion = {
  initial: { opacity: 0, y: -4, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -4, scale: 0.98 },
};

const CategorySelect = ({
  value,
  options = [],
  loading = false,
  disabled = false,
  placeholder = "Chọn danh mục dịch vụ",
  ariaLabel = "Chọn danh mục dịch vụ",
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const listboxId = useId();
  const selectedOption = useMemo(
    () => options.find((option) => option.id === value) || null,
    [options, value],
  );

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom-start",
    transform: false,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(6),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.max(180, Math.min(320, availableHeight))}px`,
          });
        },
      }),
    ],
  });

  const click = useClick(context, { enabled: !disabled && !loading });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "listbox" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const handleSelect = (option) => {
    onChange?.(option);
    setIsOpen(false);
  };

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        disabled={disabled || loading}
        className={`flex h-11 w-full items-center justify-between gap-3 rounded-lg border bg-white px-3 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[#0267FF]/30 ${
          isOpen ? "border-[#0267FF]" : "border-gray-300"
        } ${disabled || loading ? "cursor-not-allowed opacity-70" : "hover:border-[#0267FF]"}`}
        aria-label={ariaLabel}
        role="combobox"
        aria-controls={listboxId}
        aria-expanded={isOpen}
        {...getReferenceProps()}
      >
        <span
          className={`min-w-0 flex-1 truncate text-sm ${
            selectedOption ? "text-gray-900" : "text-gray-400"
          }`}
        >
          {loading ? "Đang tải danh mục..." : selectedOption?.name || placeholder}
        </span>
        <ArrowDownIcon
          className={`h-4 w-4 shrink-0 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={refs.setFloating}
              style={floatingStyles}
              id={listboxId}
              className="z-[9999] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
              {...dropdownMotion}
              transition={{ duration: 0.16, ease: "easeOut" }}
              {...getFloatingProps()}
            >
              <div className="max-h-[inherit] overflow-y-auto py-1">
                {options.length ? (
                  options.map((option) => {
                    const active = option.id === selectedOption?.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => handleSelect(option)}
                        className={`flex min-h-10 w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                          active
                            ? "bg-blue-50 text-[#0267FF]"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                          {active && <CheckIcon className="h-3 w-3" />}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{option.name}</span>
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-3 text-center text-sm text-gray-500">
                    Không có danh mục
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </>
  );
};

export default CategorySelect;
